import { Link, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { useClickOutside } from '../../hooks/useClickOutside'
import { useAuthStore } from '../../store/authStore'
import { useThemeStore } from '../../store/themeStore'
import { useLogout } from '../../api/useAuth'
import { useUnreadCount, useNotifications, useMarkRead, useMarkAllRead, useClearNotifications } from '../../api/useNotifications'
import { useSearch } from '../../api/useManga'
import { mediaUrl } from '../../utils/media'
import { formatDateTimeCompact } from '../../utils/date'
import toast from 'react-hot-toast'

export default function Header() {
  const { isAuth, user } = useAuthStore()
  const isFrozen = user?.status === 'frozen'
  const { theme, toggle: toggleTheme } = useThemeStore()
  const { mutate: logoutMutate } = useLogout()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const menuRef = useRef(null)
  const inputRef = useRef(null)
  const searchRef = useRef(null)

  const searchQuery = query.length >= 3 ? query : ''
  const { data: searchResults } = useSearch(searchQuery)
  const suggestions = (Array.isArray(searchResults) ? searchResults : searchResults?.items ?? []).slice(0, 5)

  useClickOutside(menuRef, () => setMenuOpen(false))
  useClickOutside(searchRef, () => setSearchOpen(false))

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus()
  }, [searchOpen])

  const handleLogout = () => {
    logoutMutate(undefined, {
      onSuccess: () => { toast.success('Вы вышли из аккаунта'); navigate('/') },
    })
    setMenuOpen(false)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (!query.trim()) return
    navigate(`/search?q=${encodeURIComponent(query.trim())}`)
    setSearchOpen(false)
    setQuery('')
  }

  const handleSuggestionClick = () => { setSearchOpen(false); setQuery('') }

  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)
  const { data: unreadCount = 0 } = useUnreadCount({ enabled: isAuth })
  const { data: notifications = [] } = useNotifications(false, { enabled: isAuth })
  const { mutate: markRead } = useMarkRead()
  const { mutate: markAllRead } = useMarkAllRead()
  const { mutate: clearNotifs } = useClearNotifications()

  useClickOutside(notifRef, () => setNotifOpen(false))

  const initials = user?.username?.slice(0, 2).toUpperCase() || '??'
  const avatarSrc = mediaUrl(user?.avatar_url)

  return (
    <header className="sticky top-0 z-40 bg-canvas/95 backdrop-blur-md border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
        <Link to="/" className="shrink-0 text-xl font-black text-accent">{import.meta.env.VITE_SITE_NAME}</Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          <HeaderLink to="/catalog">Каталог</HeaderLink>
          {!isFrozen && <button
            onClick={() => setSearchOpen((v) => !v)}
            className={`p-2 rounded-lg transition-colors hover:text-fg ${searchOpen ? 'text-fg' : 'text-muted'}`}
            aria-label="Поиск"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>}
        </nav>

        <div className="flex-1" />

        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg transition-colors text-muted hover:text-fg"
          title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        >
          {theme === 'dark' ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {isAuth ? (
          <>
            <nav className="hidden md:flex items-center gap-1">
              {!isFrozen && <HeaderLink to="/bookmarks">Библиотека</HeaderLink>}
              {user?.role === 'admin' && !isFrozen && <HeaderLink to="/admin" accent>Панель</HeaderLink>}

              {/* Notifications bell */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen((v) => !v)}
                  className="relative p-2 rounded-lg transition-colors text-muted hover:text-fg"
                  title="Уведомления"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold bg-accent text-on-accent rounded-full px-0.5">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <NotifDropdown
                    notifications={notifications}
                    onRead={markRead}
                    onReadAll={markAllRead}
                    onClear={clearNotifs}
                    onClose={() => setNotifOpen(false)}
                  />
                )}
              </div>

              <Link to="/profile" className="flex items-center gap-2 px-2 py-1 rounded-lg transition-colors hover:bg-surface2">
                <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-xs font-black bg-accent text-on-accent">
                  {avatarSrc
                    ? <img src={avatarSrc} alt={initials} className="w-full h-full object-cover" />
                    : initials}
                </div>
                <span className="text-sm font-semibold text-fg">{user?.username}</span>
              </Link>
            </nav>

            <div className="md:hidden relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-black shrink-0 bg-accent text-on-accent"
              >
                {avatarSrc
                  ? <img src={avatarSrc} alt={initials} className="w-full h-full object-cover" />
                  : initials}
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-10 w-44 rounded-xl py-1 shadow-2xl z-50 bg-surface border border-line">
                  <DropItem to="/profile" onClick={() => setMenuOpen(false)}>Профиль</DropItem>
                  {!isFrozen && <DropItem to="/bookmarks" onClick={() => setMenuOpen(false)}>Библиотека</DropItem>}
                  {unreadCount > 0 && (
                    <DropItem to="/profile" onClick={() => { setMenuOpen(false) }}>
                      Уведомления{' '}
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-accent text-on-accent">{unreadCount}</span>
                    </DropItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropItem to="/admin" onClick={() => setMenuOpen(false)} accent>Панель админа</DropItem>
                  )}
                  <div className="border-t border-line my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-surface2 transition-colors"
                  >
                    Выйти
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <Link to="/login" className="px-4 py-1.5 rounded-lg text-sm font-bold bg-accent text-on-accent hover:opacity-90 transition-opacity">
            Войти
          </Link>
        )}
      </div>

      {searchOpen && (
        <div className="px-4 py-2 border-t border-line" ref={searchRef}>
          <form onSubmit={handleSearch}>
            <div className="max-w-2xl mx-auto relative">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск манги..."
                className="w-full px-4 py-2 pr-10 rounded-lg text-sm outline-none bg-surface2 text-fg border border-line focus:border-accent transition-colors"
                onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
                autoComplete="off"
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </form>

          {searchQuery && suggestions.length > 0 && (
            <div className="max-w-2xl mx-auto mt-1 rounded-xl overflow-hidden shadow-2xl bg-surface border border-line">
              {suggestions.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.slug}`}
                  onClick={handleSuggestionClick}
                  className="flex items-center gap-3 px-3 py-2.5 border-b border-line hover:bg-surface2 transition-colors"
                >
                  <div className="w-8 h-12 rounded overflow-hidden shrink-0 bg-surface2">
                    {manga.cover_url && (
                      <img src={mediaUrl(manga.cover_url)} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{manga.title}</p>
                    {manga.author && <p className="text-xs text-muted truncate">{manga.author}</p>}
                  </div>
                </Link>
              ))}
              <Link
                to={`/search?q=${encodeURIComponent(searchQuery)}`}
                onClick={handleSuggestionClick}
                className="flex items-center justify-center px-3 py-2.5 text-sm font-semibold text-accent hover:bg-surface2 transition-colors"
              >
                Все результаты для «{searchQuery}» →
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  )
}

function HeaderLink({ to, children, accent }) {
  return (
    <Link
      to={to}
      className={`px-3 py-1.5 text-sm rounded-lg transition-colors hover:bg-surface2 ${accent ? 'text-accent hover:text-accent' : 'text-muted hover:text-fg'}`}
    >
      {children}
    </Link>
  )
}

function DropItem({ to, onClick, children, accent }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-4 py-2 text-sm transition-colors hover:bg-surface2 ${accent ? 'text-accent' : 'text-fg'}`}
    >
      {children}
    </Link>
  )
}

function NotifDropdown({ notifications, onRead, onReadAll, onClear, onClose }) {
  const hasUnread = notifications.some((n) => !n.is_read)

  return (
    <div className="absolute right-0 top-10 w-80 rounded-xl py-1 shadow-2xl z-50 bg-surface border border-line max-h-96 overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-2 border-b border-line">
        <span className="text-sm font-semibold">Уведомления</span>
        <div className="flex items-center gap-3">
          {hasUnread && (
            <button onClick={() => onReadAll()} className="text-xs text-accent hover:underline">Прочитать все</button>
          )}
          {notifications.length > 0 && (
            <button onClick={() => { onClear(); onClose() }} className="text-xs text-muted hover:text-red-400 transition-colors">Очистить</button>
          )}
        </div>
      </div>
      {notifications.length === 0 ? (
        <p className="text-center py-6 text-sm text-muted">Нет уведомлений</p>
      ) : (
        notifications.map((n) => (
          <div
            key={n.id}
            className={`px-4 py-3 border-b border-line last:border-0 cursor-pointer hover:bg-surface2 transition-colors ${n.is_read ? '' : 'bg-accent/5'}`}
            onClick={() => {
              if (!n.is_read) onRead(n.id)
              if (n.link) { window.location.href = n.link }
              onClose()
            }}
          >
            <div className="flex items-start gap-2">
              {!n.is_read && <span className="w-2 h-2 rounded-full bg-accent shrink-0 mt-1" />}
              <div className={n.is_read ? '' : 'pl-0'}>
                <p className="text-sm font-semibold">{n.title}</p>
                <p className="text-xs text-muted mt-0.5">{n.text}</p>
                <p className="text-xs text-muted mt-1">{formatDateTimeCompact(n.created_at)}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )
}
